'use client';

import { notifications } from '@mantine/notifications';
import { useCallback, useState, useTransition } from 'react';

import { AppError, ErrorType } from '@/utils/error';
import rollbar from '@/utils/rollbar';

import type { ServerActionResult } from '@/utils/server-actions';

export type TransitionOptions = {
  skipErrorNotification?: boolean;
  successTitle?: string;
  successMessage?: string;
};

export function useSafeTransition(
  name: string,
  { skipErrorNotification, successMessage, successTitle }: TransitionOptions = {},
) {
  const [error, setError] = useState<AppError | null>(null);
  const [pending, startTransition] = useTransition();

  const startSafeTransition = useCallback(
    (callback: React.TransitionFunction) => {
      setError(null);
      startTransition(async () => {
        try {
          await callback();

          if (successMessage) {
            notifications.show({
              title: successTitle || 'Operação realizada com sucesso',
              message: successMessage,
              color: 'green',
              autoClose: 6000,
            });
          }
        } catch (error) {
          // Let the redirect error pass through
          if ((error as { digest?: string }).digest?.includes('NEXT_REDIRECT')) {
            throw error;
          }

          let appError: AppError;
          if (error instanceof AppError) {
            appError = error;
          } else if (error instanceof Error && error.name === 'APP_ERROR') {
            appError = AppError.fromSerializedError(error);
          } else {
            appError = new AppError(
              ErrorType.InternalError,
              'Ocorreu um erro inesperado. Tente novamente mais tarde.',
              { cause: error as Error },
            );
          }

          setError(appError);

          if (appError.shouldLog()) {
            rollbar.error(`Error during "${name}" action:`, appError);
          }

          // Show notification only if not skipped and no specific error handling
          if (!skipErrorNotification) {
            const message = appError.getUserMessage();
            notifications.show({
              title: 'Ocorreu um erro',
              message,
              color: 'red',
              autoClose: 6000,
            });
          }
        }
      });
    },
    [skipErrorNotification, successMessage, successTitle, name],
  );

  const clearError = useCallback(() => setError(null), []);

  return [pending, startSafeTransition, error, clearError] as const;
}

export async function perform<Return, Args extends unknown[]>(
  action: (...args: Args) => Promise<ServerActionResult<Return>>,
  ...args: Args
) {
  const result = await action(...args);
  if (result.success) {
    return result.data;
  } else {
    throw new AppError(result.error.type, result.error.message, {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      details: result.error.details as any,
    });
  }
}
